import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Flame, ChevronRight, ChevronLeft, MessageSquare, ClipboardList, Palette, Zap, Heart, Bell, Shield } from "lucide-react";
import { CoachingStyle, AppTheme, DEFAULT_THEME, THEME_BACKGROUNDS, THEME_ACCENTS, THEME_EDGES, THEME_FONTS } from "@/types/evolve";
import { StatsStep } from "./onboarding/StatsStep";
import { TrainingDetailsStep } from "./onboarding/TrainingDetailsStep";
import { GoalStep } from "./onboarding/GoalStep";
import { NotificationsStep } from "./onboarding/NotificationsStep";
import { MotivationStep } from "./onboarding/MotivationStep";
import { CustomizationStep } from "./onboarding/CustomizationStep";
import { AgeGateStep } from "./onboarding/AgeGateStep";

interface UserStats {
  age?: number;
  sex?: string;
  height?: string;
  weight?: number;
  goal?: string;
  specificGoal?: string;
  experience?: string;
  daysPerWeek?: number;
  timePerSession?: string;
  preferredTrainingTime?: string;
  equipment?: string;
  injuries?: string;
  dietPreferences?: string;
  displayName?: string;
  leaderboardPrivacy?: string;
  ageVerified?: boolean;
}

interface OnboardingProps {
  onComplete: (coachingStyle: CoachingStyle, theme: AppTheme, motivation?: string, stats?: UserStats) => void;
  onRequestNotifications: () => Promise<boolean>;
}

type StepType = 'age-gate' | 'stats' | 'training' | 'goal' | 'notifications' | 'motivation' | 'customization';

interface Step {
  icon: typeof MessageSquare;
  title: string;
  description: string;
  type: StepType;
}

const STEPS: Step[] = [
  {
    icon: Shield,
    title: "Age Verification",
    description: "Evolve is designed for users 13 and older.",
    type: 'age-gate',
  },
  {
    icon: MessageSquare,
    title: "Your Stats",
    description: "Basic info so I can personalize everything.",
    type: 'stats',
  },
  {
    icon: ClipboardList,
    title: "Training Details",
    description: "Tell me about your training setup and preferences.",
    type: 'training',
  },
  {
    icon: ClipboardList,
    title: "Your Goal",
    description: "Be specific — the clearer your target, the better your plan.",
    type: 'goal',
  },
  {
    icon: Bell,
    title: "Unlock Your Daily Evolution Nudges",
    description: "Stay accountable with daily reminders.",
    type: 'notifications',
  },
  {
    icon: Heart,
    title: "Your Why",
    description: "What's your biggest motivation? This helps me tailor everything even more.",
    type: 'motivation',
  },
  {
    icon: Palette,
    title: "Customize Your Coach",
    description: "Make Evolve yours. Choose your coach personality and customize the app look.",
    type: 'customization',
  },
];

export function Onboarding({ onComplete, onRequestNotifications }: OnboardingProps) {
  const [step, setStep] = useState(0);
  const [coachingStyle, setCoachingStyle] = useState<CoachingStyle>('chill-veteran');
  const [theme, setTheme] = useState<AppTheme>(DEFAULT_THEME);
  const [motivation, setMotivation] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [activeCustomizationTab, setActiveCustomizationTab] = useState<'coach' | 'theme' | 'privacy'>('coach');
  const [displayName, setDisplayName] = useState('');
  const [leaderboardPrivacy, setLeaderboardPrivacy] = useState('full_name');
  
  // Age gate state
  const [isAgeVerified, setIsAgeVerified] = useState(false);
  
  // Stats state
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [goal, setGoal] = useState('');
  const [specificGoal, setSpecificGoal] = useState('');
  const [experience, setExperience] = useState('');
  const [daysPerWeek, setDaysPerWeek] = useState('');
  const [timePerSession, setTimePerSession] = useState('');
  const [preferredTrainingTime, setPreferredTrainingTime] = useState('');
  const [equipment, setEquipment] = useState('');
  const [injuries, setInjuries] = useState('');
  const [dietPreferences, setDietPreferences] = useState('');

  // Apply theme in real-time as user changes options
  const applyThemePreview = useCallback((newTheme: AppTheme) => {
    const root = document.documentElement;
    
    const bg = THEME_BACKGROUNDS.find(b => b.value === newTheme.background);
    if (bg) {
      root.style.setProperty('--background', bg.hsl);
      root.style.setProperty('--evolve-black', bg.hsl);
      const [h, s, l] = bg.hsl.split(' ');
      const lightness = parseFloat(l) + 2;
      root.style.setProperty('--card', `${h} ${s} ${lightness}%`);
      root.style.setProperty('--popover', `${h} ${s} ${lightness}%`);
    }
    
    const accent = THEME_ACCENTS.find(a => a.value === newTheme.accent);
    if (accent) {
      root.style.setProperty('--primary', accent.hsl);
      root.style.setProperty('--accent', accent.hsl);
      root.style.setProperty('--ring', accent.hsl);
      root.style.setProperty('--evolve-red', accent.hsl);
    }
    
    const edge = THEME_EDGES.find(e => e.value === newTheme.edge);
    if (edge) {
      root.style.setProperty('--radius', edge.radius);
    }
    
    const font = THEME_FONTS.find(f => f.value === newTheme.font);
    if (font) {
      root.style.setProperty('--font-display', font.display);
      root.style.setProperty('--font-body', font.body);
      document.body.style.fontFamily = `${font.body}, sans-serif`;
      document.querySelectorAll('h1, h2, h3, h4, h5, h6, .font-display').forEach((el) => {
        (el as HTMLElement).style.fontFamily = `${font.display}, sans-serif`;
      });
    }
  }, []);

  useEffect(() => {
    applyThemePreview(theme);
  }, [theme, applyThemePreview]);

  const handleThemeChange = useCallback((updates: Partial<AppTheme>) => {
    setTheme(prev => ({ ...prev, ...updates }));
  }, []);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = () => {
    const stats: UserStats = {
      age: age ? parseInt(age) : undefined,
      sex: sex || undefined,
      height: height || undefined,
      weight: weight ? parseInt(weight) : undefined,
      goal: goal || undefined,
      specificGoal: specificGoal || undefined,
      experience: experience || undefined,
      daysPerWeek: daysPerWeek ? parseInt(daysPerWeek) : undefined,
      timePerSession: timePerSession || undefined,
      preferredTrainingTime: preferredTrainingTime || undefined,
      equipment: equipment || undefined,
      injuries: injuries || undefined,
      dietPreferences: dietPreferences || undefined,
      displayName: displayName.trim() || undefined,
      leaderboardPrivacy: leaderboardPrivacy || undefined,
      ageVerified: isAgeVerified,
    };
    onComplete(coachingStyle, theme, motivation.trim() || undefined, stats);
  };

  const handleEnableNotifications = async () => {
    try {
      console.log('[Onboarding] Requesting notifications...');
      const result = await onRequestNotifications();
      console.log('[Onboarding] Notification result:', result);
      setNotificationsEnabled(result);
      
      // Automatically advance to next step after a delay if enabled
      if (result) {
        setTimeout(() => {
          handleNext();
        }, 1500);
      }
    } catch (error) {
      console.error('[Onboarding] Notification request error:', error);
      setNotificationsEnabled(false);
    }
  };

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  // Check if Next button should be disabled (age gate must be verified)
  const isNextDisabled = currentStep.type === 'age-gate' && !isAgeVerified;

  const renderStepContent = () => {
    switch (currentStep.type) {
      case 'age-gate':
        return (
          <AgeGateStep
            icon={currentStep.icon}
            title={currentStep.title}
            description={currentStep.description}
            isAgeVerified={isAgeVerified}
            setIsAgeVerified={setIsAgeVerified}
          />
        );
      case 'stats':
        return (
          <StatsStep
            icon={currentStep.icon}
            title={currentStep.title}
            description={currentStep.description}
            age={age}
            setAge={setAge}
            sex={sex}
            setSex={setSex}
            height={height}
            setHeight={setHeight}
            weight={weight}
            setWeight={setWeight}
            experience={experience}
            setExperience={setExperience}
          />
        );
      case 'training':
        return (
          <TrainingDetailsStep
            icon={currentStep.icon}
            title={currentStep.title}
            description={currentStep.description}
            daysPerWeek={daysPerWeek}
            setDaysPerWeek={setDaysPerWeek}
            timePerSession={timePerSession}
            setTimePerSession={setTimePerSession}
            preferredTrainingTime={preferredTrainingTime}
            setPreferredTrainingTime={setPreferredTrainingTime}
            equipment={equipment}
            setEquipment={setEquipment}
            injuries={injuries}
            setInjuries={setInjuries}
            dietPreferences={dietPreferences}
            setDietPreferences={setDietPreferences}
          />
        );
      case 'goal':
        return (
          <GoalStep
            icon={currentStep.icon}
            title={currentStep.title}
            description={currentStep.description}
            goal={goal}
            setGoal={setGoal}
            specificGoal={specificGoal}
            setSpecificGoal={setSpecificGoal}
          />
        );
      case 'notifications':
        return (
          <NotificationsStep
            title={currentStep.title}
            notificationsEnabled={notificationsEnabled}
            onEnableNotifications={handleEnableNotifications}
            onSkip={handleNext}
          />
        );
      case 'motivation':
        return (
          <MotivationStep
            icon={currentStep.icon}
            title={currentStep.title}
            description={currentStep.description}
            motivation={motivation}
            setMotivation={setMotivation}
          />
        );
      case 'customization':
        return (
          <CustomizationStep
            icon={currentStep.icon}
            title={currentStep.title}
            description={currentStep.description}
            coachingStyle={coachingStyle}
            setCoachingStyle={setCoachingStyle}
            theme={theme}
            onThemeChange={handleThemeChange}
            activeTab={activeCustomizationTab}
            setActiveTab={setActiveCustomizationTab}
            displayName={displayName}
            setDisplayName={setDisplayName}
            leaderboardPrivacy={leaderboardPrivacy}
            setLeaderboardPrivacy={setLeaderboardPrivacy}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full evolve-gradient flex items-center justify-center">
            <Flame className="w-4 h-4 text-foreground" />
          </div>
          <span className="font-display text-lg font-bold">EVOLVE</span>
        </div>
        <button 
          onClick={() => onComplete('drill-sergeant', DEFAULT_THEME)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip
        </button>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2 py-4">
        {STEPS.map((_, index) => (
          <div
            key={index}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === step 
                ? 'w-8 bg-primary' 
                : index < step 
                  ? 'w-2 bg-primary/50' 
                  : 'w-2 bg-border'
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-6 py-4 overflow-y-auto">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="p-6 pb-8">
        <div className="flex gap-3">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex-1"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
          
          {isLastStep ? (
            <Button
              variant="evolve"
              onClick={handleComplete}
              className="flex-1"
            >
              Start Free Trial
              <Zap className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              variant="evolve"
              onClick={handleNext}
              className="flex-1"
              disabled={isNextDisabled}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
